// src/sim/round/composition.ts — P4 round composition (D-35..D-39).
// Deterministic (seeded via rng.round); within-round only (resets at round-started per D-33).
//
// Source: .planning/phases/04-ai/04-CONTEXT.md D-35..D-39; 04-RESEARCH.md Pattern 15.

export interface RosterComposition {
  hunters: string[];
  hiders: string[];
  benched: string[];
}

/**
 * Pick one NPC to bench for the round, avoiding the previous round's benched NPC
 * (anti-consecutive D-39). Player is never in `npcIds` (caller responsibility, D-36).
 *
 * Returns `null` only when `npcIds` is empty (e.g. Benny-unlocked 5v5 case).
 */
export function pickBenchedNpc(
  npcIds: string[],
  lastBenchedNpcId: string | null,
  roundRng: () => number,
): string | null {
  if (npcIds.length === 0) return null;
  if (npcIds.length === 1) return npcIds[0]!;
  // Reroll up to 10 times to avoid consecutive repeat (D-39 anti-consecutive guard).
  for (let i = 0; i < 10; i++) {
    const pick = npcIds[Math.floor(roundRng() * npcIds.length)]!;
    if (pick !== lastBenchedNpcId) return pick;
  }
  // Pathological fall-through: all rerolls hit the same id (only possible with 1 candidate
  // after filtering, which is impossible with npcIds.length > 1 unless lastBenchedNpcId
  // fills every slot). Return first non-last, else first.
  return npcIds.find((id) => id !== lastBenchedNpcId) ?? npcIds[0]!;
}

/**
 * Compose the initial roster for a round.
 *
 * - `playerCharacterId`: the player's character id (never benched, D-36).
 * - `playerRole`: which team the player is on.
 * - `allPlayableIds`: ALL character ids including player + benny (if present).
 * - `bennyLocked`: true → 9-pool (4v4 + 1 benched NPC); false → 10-pool (5v5, 0 benched).
 * - `lastBenchedNpcId`: from WorldState.lastBenchedNpcId for anti-consecutive (D-39).
 * - `roundRng`: the round sub-seed RNG (SimRng.round), consumed per-round.
 *
 * When `bennyLocked=true`:
 *   - benny is excluded from the active pool entirely (P5 handles unlock UI).
 *   - 8 NPCs remain after removing player + benny.
 *   - 1 NPC is benched; 7 NPCs + player = 8 active characters → 4v4 split.
 *
 * When `bennyLocked=false`:
 *   - All 10 playable characters (player + 9 NPCs) are active.
 *   - 0 benched; 10 active = 5v5.
 */
export function composeInitialRoster(
  playerCharacterId: string,
  playerRole: 'hunter' | 'hider',
  allPlayableIds: string[],
  bennyLocked: boolean,
  lastBenchedNpcId: string | null,
  roundRng: () => number,
): RosterComposition {
  // Build the NPC pool: exclude player, and exclude benny when locked.
  const npcIds = allPlayableIds.filter(
    (id) => id !== playerCharacterId && (bennyLocked ? id !== 'benny' : true),
  );

  const benched: string[] = [];
  let npcPool = [...npcIds];

  if (bennyLocked) {
    // 8 NPCs available (9 total - 1 benny excluded); bench 1 so 7 NPCs remain.
    // With the player that's 8 active (4 hunters + 4 hiders).
    const benchedId = pickBenchedNpc(npcPool, lastBenchedNpcId, roundRng);
    if (benchedId !== null) {
      benched.push(benchedId);
      npcPool = npcPool.filter((id) => id !== benchedId);
    }
  }
  // bennyLocked=false: 9 NPCs + player = 10 active; no bench needed.

  // Shuffle npcPool via round RNG for deterministic assignment.
  const shuffled = [...npcPool];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(roundRng() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
  }

  // Compute how many NPC slots each team needs (player fills one slot on their team).
  const totalHunters = bennyLocked ? 4 : 5;
  const totalHiders = bennyLocked ? 4 : 5;
  const neededHuntersNpc = playerRole === 'hunter' ? totalHunters - 1 : totalHunters;
  const neededHidersNpc = playerRole === 'hider' ? totalHiders - 1 : totalHiders;

  const hunters = shuffled.slice(0, neededHuntersNpc);
  const hiders = shuffled.slice(neededHuntersNpc, neededHuntersNpc + neededHidersNpc);

  // Add player to their team.
  if (playerRole === 'hunter') hunters.push(playerCharacterId);
  else hiders.push(playerCharacterId);

  hunters.sort();
  hiders.sort();

  return { hunters, hiders, benched };
}
